/**
 * Authentication hook
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store';
import { apiEndpoints } from '@/lib/api';
import { getSignInUrl, getSignUpUrl } from '@/lib/workos';

export function useAuth() {
  const user = useStore((state) => state.user);
  const accessToken = useStore((state) => state.accessToken);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const isLoading = useStore((state) => state.isLoading);
  const logout = useStore((state) => state.logout);
  const updateUser = useStore((state) => state.updateUser);

  /**
   * Sign in with WorkOS
   */
  const signIn = (returnTo?: string) => {
    window.location.href = getSignInUrl(returnTo);
  };

  /**
   * Sign up with WorkOS
   */
  const signUp = (returnTo?: string) => {
    window.location.href = getSignUpUrl(returnTo);
  };

  /**
   * Sign out
   */
  const signOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * Refresh user data
   */
  const refreshUser = async () => {
    if (!accessToken) return;

    try {
      const { user } = await apiEndpoints.auth.me();
      updateUser(user);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  /**
   * Check authentication status on mount
   * Disabled for now since we're using memory-only storage
   * and the auth state is already set by AuthCallback
   */
  // useEffect(() => {
  //   const checkAuth = async () => {
  //     if (!accessToken) {
  //       useAuthStore.setState({ isLoading: false })
  //       return
  //     }

  //     try {
  //       const { user } = await apiEndpoints.auth.me()
  //       login(user, accessToken)
  //     } catch (error) {
  //       console.error('Auth check failed:', error)
  //       logout()
  //     }
  //   }

  //   checkAuth()
  // }, [])

  return {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    signUp,
    signOut,
    refreshUser,
  };
}

/**
 * Hook to require authentication
 */
export function useRequireAuth(redirectTo = '/login') {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  return { isAuthenticated, isLoading };
}
